import {
  TEXT_CONTEXT_LEN,
  type Anchor,
  type PinAnchor,
  type RegionAnchor,
  type TextAnchor,
} from '@annotate/shared';
import { hasBox, isOurs, resolveSelectors, selectorsFor } from './selector.js';
import {
  buildTextIndex,
  findQuote,
  globalOffset,
  rangeFromGlobal,
  type TextIndex,
} from './text-index.js';

export interface PageRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Placement {
  /** Where the numbered marker goes, in page coordinates. */
  x: number;
  y: number;
  /** Boxes to highlight. Empty for pins. */
  rects: PageRect[];
}

export type Resolution = { ok: true; placement: Placement } | { ok: false; reason: string };

function toPageRect(r: DOMRect): PageRect {
  return { x: r.left + window.scrollX, y: r.top + window.scrollY, w: r.width, h: r.height };
}

function contextOf(el: Element): string {
  return (el.textContent ?? '').replace(/\s+/g, ' ').trim().slice(0, 160);
}

// ─── capture ────────────────────────────────────────────────────────────────

/**
 * The nearest enclosing `data-annotate-id`, walking up from the anchored element.
 *
 * Captured separately from the selector even though `selectorsFor` will happily
 * use the attribute when the anchored element carries it directly. Most comments
 * land on a paragraph or a word *inside* a labelled section, and in that case the
 * selector is a CSS path and the section is lost — which is precisely the case
 * where someone reading the comment later needs to know which section it was.
 *
 * Recorded at capture time rather than derived at read time, because by the time
 * anyone asks, the page has usually been regenerated.
 */
function sectionIdFor(el: Element): string | null {
  const labelled = el.closest('[data-annotate-id]');
  return labelled?.getAttribute('data-annotate-id') ?? null;
}

export function capturePin(clientX: number, clientY: number): PinAnchor | null {
  const el = elementAt(clientX, clientY);
  if (!el) return null;

  const rect = el.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return null;

  return {
    kind: 'pin',
    sectionId: sectionIdFor(el),
    selectors: selectorsFor(el),
    offsetPct: {
      x: clamp((clientX - rect.left) / rect.width),
      y: clamp((clientY - rect.top) / rect.height),
    },
    context: contextOf(el),
  };
}

export function captureText(range: Range, index: TextIndex): TextAnchor | null {
  const quote = range.toString();
  if (!quote.trim()) return null;

  const start = globalOffset(index, range.startContainer, range.startOffset);
  const end = globalOffset(index, range.endContainer, range.endOffset);
  if (start === null || end === null || end <= start) return null;

  const container =
    range.commonAncestorContainer instanceof Element
      ? range.commonAncestorContainer
      : range.commonAncestorContainer.parentElement;
  if (!container) return null;

  return {
    kind: 'text',
    sectionId: sectionIdFor(container),
    selectors: selectorsFor(container),
    quote,
    prefix: index.text.slice(Math.max(0, start - TEXT_CONTEXT_LEN), start),
    suffix: index.text.slice(end, end + TEXT_CONTEXT_LEN),
  };
}

/** `rect` is in viewport coordinates. */
export function captureRegion(rect: PageRect): RegionAnchor | null {
  if (rect.w < 6 || rect.h < 6) return null;

  const centre = elementAt(rect.x + rect.w / 2, rect.y + rect.h / 2);
  if (!centre) return null;

  // Walk up to the first ancestor that actually contains the drag. Anchoring a
  // region to something smaller than itself makes the stored percentages fall
  // outside 0..1 and the box reappears in the wrong place after any reflow.
  let host: Element = centre;
  while (host.parentElement && !containsRect(host.getBoundingClientRect(), rect)) {
    host = host.parentElement;
  }

  const box = host.getBoundingClientRect();
  if (box.width === 0 || box.height === 0) return null;

  return {
    kind: 'region',
    sectionId: sectionIdFor(host),
    selectors: selectorsFor(host),
    rectPct: {
      x: (rect.x - box.left) / box.width,
      y: (rect.y - box.top) / box.height,
      w: rect.w / box.width,
      h: rect.h / box.height,
    },
    context: contextOf(host),
  };
}

// ─── resolution ─────────────────────────────────────────────────────────────

/**
 * Work out where an anchor sits on the page as it is right now.
 *
 * Every failure path returns a reason instead of a position. The caller puts
 * those in the orphan tray, where the comment stays readable and actionable.
 * The one thing this must never do is return a plausible-looking coordinate it
 * is not sure about — a marker on the wrong paragraph sends someone to fix the
 * wrong thing, and nothing about the UI would suggest anything was amiss.
 */
export function resolveAnchor(anchor: Anchor, index: TextIndex): Resolution {
  switch (anchor.kind) {
    case 'pin':
      return resolvePin(anchor);
    case 'text':
      return resolveText(anchor, index);
    case 'region':
      return resolveRegion(anchor);
  }
}

function resolvePin(anchor: PinAnchor): Resolution {
  const el = resolveSelectors(anchor.selectors);
  if (!el) return { ok: false, reason: 'the element it was attached to is no longer on the page' };
  if (!hasBox(el)) return { ok: false, reason: 'the element it was attached to is hidden' };

  const rect = el.getBoundingClientRect();
  return {
    ok: true,
    placement: {
      x: rect.left + window.scrollX + anchor.offsetPct.x * rect.width,
      y: rect.top + window.scrollY + anchor.offsetPct.y * rect.height,
      rects: [],
    },
  };
}

function resolveText(anchor: TextAnchor, index: TextIndex): Resolution {
  const match = findQuote(index, anchor.quote, anchor.prefix, anchor.suffix);
  if (!match) {
    return index.text.includes(anchor.quote)
      ? { ok: false, reason: 'this text now appears several times and the surrounding wording changed' }
      : { ok: false, reason: 'the text it quoted is no longer on the page' };
  }

  const range = rangeFromGlobal(index, match.start, match.end);
  if (!range) return { ok: false, reason: 'the quoted text could not be located in the page' };

  const rects = Array.from(range.getClientRects())
    .filter((r) => r.width > 0 && r.height > 0)
    .map(toPageRect);
  if (rects.length === 0) return { ok: false, reason: 'the quoted text is present but not visible' };

  const first = rects[0]!;
  return { ok: true, placement: { x: first.x, y: first.y, rects } };
}

function resolveRegion(anchor: RegionAnchor): Resolution {
  const el = resolveSelectors(anchor.selectors);
  if (!el) return { ok: false, reason: 'the section it framed is no longer on the page' };
  if (!hasBox(el)) return { ok: false, reason: 'the section it framed is hidden' };

  const box = el.getBoundingClientRect();
  const rect: PageRect = {
    x: box.left + window.scrollX + anchor.rectPct.x * box.width,
    y: box.top + window.scrollY + anchor.rectPct.y * box.height,
    w: anchor.rectPct.w * box.width,
    h: anchor.rectPct.h * box.height,
  };
  return { ok: true, placement: { x: rect.x, y: rect.y, rects: [rect] } };
}

// ─── helpers ────────────────────────────────────────────────────────────────

/** Hit-test that ignores our own overlay, so clicking "through" the review UI
 *  lands on the page content underneath. */
function elementAt(x: number, y: number): Element | null {
  const stack = document.elementsFromPoint(x, y);
  for (const el of stack) {
    if (isOurs(el)) continue;
    if (el === document.documentElement || el === document.body) continue;
    return el;
  }
  return document.body;
}

function containsRect(outer: DOMRect, inner: PageRect): boolean {
  return (
    outer.left <= inner.x + 1 &&
    outer.top <= inner.y + 1 &&
    outer.right >= inner.x + inner.w - 1 &&
    outer.bottom >= inner.y + inner.h - 1
  );
}

function clamp(n: number): number {
  return Math.max(0, Math.min(1, n));
}

export { buildTextIndex };
export type { TextIndex };
